import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Laptop, Home, Car, Check, Info, Clock, HeartHandshake, Download, FileText, ArrowRight } from 'lucide-react';
import { pricing, siteConfig } from '../data';
import { motion } from 'framer-motion';

export default function PricingSection() {
  return (
    <section id="priser" className="py-20 bg-slate-50 border-t border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-blue-600 font-bold text-sm uppercase tracking-wider block mb-2">
            Gennemskuelige priser
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#103B66] mb-4">
            Priser
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Hos os er der ingen skjulte gebyrer eller ubehagelige overraskelser. Alle priser er inklusiv moms.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          
          {/* Card 1: Telefon & Fjernsupport */}
          <div className="bg-white border border-slate-100 shadow-lg p-8 flex flex-col justify-between relative">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-blue-50 text-blue-600">
                  <Laptop className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#103B66]">{pricing.rates[0].title}</h3>
                  <p className="text-xs text-slate-400">Hurtig hjælp på distancen</p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold text-slate-900">{pricing.rates[0].price}</span>
                  <span className="text-slate-500 text-sm font-medium">{pricing.rates[0].unit}</span>
                </div>
                <p className="text-xs text-blue-600 font-semibold mt-1">{pricing.billingInterval}</p>
              </div>

              <p className="text-slate-600 text-sm mb-8 leading-relaxed">
                {pricing.rates[0].description}
              </p>

              <ul className="space-y-3.5 mb-8">
                {pricing.rates[0].features.map((feature, i) => (
                  <li key={i} className="flex items-start text-sm text-slate-600">
                    <Check className="w-5 h-5 text-green-500 mr-2.5 shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* TeamViewer Download Link */}
              <div className="mb-8 p-3.5 bg-blue-50/50 border border-blue-100/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="text-xs font-semibold text-slate-700">Klar til fjernsupport?</span>
                </div>
                <a
                  href="https://www.teamviewer.com/da/download/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline shrink-0"
                >
                  Hent TeamViewer her
                </a>
              </div>
            </div>

            <a
              href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
              className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-[#103B66] text-white font-bold hover:bg-slate-800 transition-all text-center text-sm shadow-sm"
            >
              <Phone className="w-4 h-4 mr-2" />
              Få hjælp nu
            </a>
          </div>

          {/* Card 2: Personligt Fremmøde */}
          <div className="bg-white border-2 border-blue-600 shadow-xl p-8 flex flex-col justify-between relative">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 uppercase tracking-wider">
              Mest populære
            </div>

            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-blue-50 text-blue-600">
                  <Home className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#103B66]">{pricing.rates[1].title}</h3>
                  <p className="text-xs text-slate-400">Hjælp hjemme hos dig selv</p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold text-slate-900">{pricing.rates[1].price}</span>
                  <span className="text-slate-500 text-sm font-medium">{pricing.rates[1].unit}</span>
                </div>
                <p className="text-xs text-blue-600 font-semibold mt-1">{pricing.billingInterval}</p>
              </div>

              <p className="text-slate-600 text-sm mb-8 leading-relaxed">
                {pricing.rates[1].description}
              </p>

              <ul className="space-y-3.5 mb-8">
                {pricing.rates[1].features.map((feature, i) => (
                  <li key={i} className="flex items-start text-sm text-slate-600">
                    <Check className="w-5 h-5 text-green-500 mr-2.5 shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
              className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all text-center text-sm shadow-sm"
            >
              <Phone className="w-4 h-4 mr-2" />
              Book et hjemmebesøg
            </a>
          </div>

        </div>

        {/* Driving and Terms Info Box */}
        <div className="max-w-5xl mx-auto bg-white border border-slate-100 shadow-md p-6 sm:p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-none shrink-0">
              <Car className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-bold text-[#103B66] mb-2">Kørsel og transportvilkår</h4>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                {pricing.drivingTerms}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2.5 text-xs text-slate-500">
                  <Info className="w-4 h-4 text-blue-500 shrink-0" />
                  <span>{pricing.vatIncluded}</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-slate-500">
                  <Info className="w-4 h-4 text-blue-500 shrink-0" />
                  <span>{pricing.billingInterval}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* First Call & Customer Setup Banner */}
        <div className="max-w-5xl mx-auto bg-gradient-to-r from-[#103B66] to-slate-900 text-white p-8 mb-12 relative overflow-hidden">
          <div className="absolute right-0 top-0 translate-x-1/3 -translate-y-1/3 w-96 h-96 bg-blue-600/10 rounded-full pointer-events-none" />
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider mb-4">
                <Clock className="w-3.5 h-3.5" />
                Kom godt i gang
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold mb-3">
                Første samtale til kun <span className="text-blue-400">99,-</span> (ca. 10-15 min.)
              </h3>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-3">
                Første samtale koster de 99 kr. (ca. 10-15 minutter). Her tager vi hånd om din situation og rådgiver om, hvordan du beskytter dig.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed">
                Ved første henvendelse opretter vi dig som kunde i vores system, så vi altid kan se, om du har ringet før, og sikre dig hurtig og genkendelig hjælp fremover.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-6 py-3.5 bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all text-sm shadow-md"
              >
                <Phone className="w-4 h-4 mr-2" />
                Ring og book nu
              </a>
            </div>
          </div>
        </div>

        {/* Business Terms Banner */}
        <div className="max-w-5xl mx-auto bg-white border-2 border-slate-200 shadow-md p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 shrink-0">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-[#103B66] mb-1">Gældende forretningsbetingelser</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Her kan du læse vores officielle forretningsbetingelser for Online Tryghed ApS direkte på hjemmesiden, herunder betalingsvilkår, henvendelser og ansvarsforhold.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Link
              to="/betingelser"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#103B66] hover:bg-slate-800 text-white text-sm font-semibold transition-colors shadow-sm"
            >
              <span>Læs betingelser</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="/_assets/forretningsbetingelser-for-online-tryghed-aps.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-3 text-slate-500 hover:text-slate-800 text-xs font-medium transition-colors"
              title="Hent som PDF"
            >
              <Download className="w-3.5 h-3.5" />
              <span>PDF</span>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
